import React, { useEffect, useMemo, useState } from 'react';

import { Button, Modal } from '@copilot/ui';
import Icon from '../common/Icon';
import * as companyApi from '../../services/companyApi';
import * as meetingApi from '../../services/meetingApi';

function formatDate(isoString) {
  if (!isoString) return '';

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// "Choose previous meetings" (context_mode = MANUAL, backend/models/
// meeting_context_source.py) - reuses the EXISTING GET /api/meetings
// list (search + pagination already built in, see backend/repositories/
// meeting_repository.py's list_for_user) rather than a new endpoint,
// and the existing GET /api/companies purely to resolve each result's
// company name for display.
function PreviousMeetingsPicker({ selectedMeetingIds, onChange, onClose }) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [companiesById, setCompaniesById] = useState({});

  useEffect(() => {
    companyApi
      .getCompanies()
      .then((companies) => {
        setCompaniesById(Object.fromEntries(companies.map((c) => [c.id, c.name])));
      })
      .catch(() => {
        // Best-effort: company names are a display nicety only - a
        // failure here must never block picking previous meetings.
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const trimmedQuery = query.trim();

    const timeout = setTimeout(
      async () => {
        setIsLoading(true);
        setError('');

        try {
          const data = await meetingApi.listMeetings({
            search: trimmedQuery || undefined,
            page,
            pageSize: 10,
          });
          if (cancelled) return;

          setItems((current) => (page === 1 ? data.items : [...current, ...data.items]));
          setTotalPages(data.total_pages || 1);
        } catch (err) {
          if (!cancelled) setError(err.message || 'Unable to load meetings.');
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      },
      trimmedQuery ? 250 : 0
    );

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, page]);

  // A new search always restarts from page 1.
  useEffect(() => {
    setPage(1);
  }, [query]);

  const selectedSet = useMemo(() => new Set(selectedMeetingIds), [selectedMeetingIds]);

  const toggle = (meetingId) => {
    if (selectedSet.has(meetingId)) {
      onChange(selectedMeetingIds.filter((id) => id !== meetingId));
    } else {
      onChange([...selectedMeetingIds, meetingId]);
    }
  };

  return (
    <Modal
      title="Select Previous Meetings"
      subtitle="Choose specific previous meetings for KrishvaQ to use as context."
      onClose={onClose}
      size="lg"
      actions={
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="previous-meetings-picker">
        <div className="document-selector-search">
          <Icon name="search" size={17} />
          <input className="input"
            aria-label="Search meetings"
            placeholder="Search meetings..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        {error && <div className="form-alert">{error}</div>}

        {isLoading && items.length === 0 && <p className="field-help">Loading meetings...</p>}

        {!isLoading && items.length === 0 && !error && (
          <p className="field-help">No meetings found.</p>
        )}

        {items.length > 0 && (
          <ul className="previous-meetings-picker-list">
            {items.map((meeting) => {
              const companyName = meeting.company_id
                ? companiesById[meeting.company_id]
                : null;

              return (
                <li key={meeting.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(meeting.id)}
                      onChange={() => toggle(meeting.id)}
                    />
                    <span>
                      <strong>{meeting.title}</strong>
                      <span className="field-help">
                        {formatDate(meeting.created_at)}
                        {companyName ? ` · ${companyName}` : ''}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        {page < totalPages && (
          <Button
            type="button"
            variant="secondary"
            disabled={isLoading}
            onClick={() => setPage((p) => p + 1)}
          >
            Load more
          </Button>
        )}

        <p className="field-help previous-meetings-picker-count">
          Selected: {selectedMeetingIds.length} meeting
          {selectedMeetingIds.length === 1 ? '' : 's'}
        </p>
      </div>
    </Modal>
  );
}

export default PreviousMeetingsPicker;
